import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import type { Offer, Project, OfferPosition } from '../types';

const emptyPos = (): OfferPosition => ({ posNumber: '', description: '', unit: 'Stk', quantity: 1, unitPrice: 0 });

const statusLabels: Record<string, string> = {
  DRAFT: 'Entwurf', SENT: 'Gesendet', ACCEPTED: 'Angenommen', REJECTED: 'Abgelehnt', CANCELLED: 'Storniert',
};
const statusStyles: Record<string, string> = {
  DRAFT:     'bg-slate-100 text-slate-600',
  SENT:      'bg-blue-50 text-blue-700 border border-blue-200',
  ACCEPTED:  'bg-emerald-50 text-emerald-700 border border-emerald-200',
  REJECTED:  'bg-red-50 text-red-600 border border-red-200',
  CANCELLED: 'bg-slate-100 text-slate-400',
};

export default function OffersPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<(Partial<Offer> & { positions?: OfferPosition[] }) | null>(null);

  const { data: offers = [], isLoading } = useQuery<Offer[]>({
    queryKey: ['offers'],
    queryFn: () => api.get('/offers').then((r) => r.data),
  });

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects').then((r) => r.data),
  });

  const saveMutation = useMutation({
    mutationFn: (o: Partial<Offer>) =>
      o.id ? api.put(`/offers/${o.id}`, o) : api.post('/offers', o),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['offers'] }); setEditing(null); },
  });

  const fmt = (n: number) => Number(n).toFixed(2).replace('.', ',');

  const updatePos = (i: number, field: string, value: string | number) => {
    const p = [...(editing?.positions || [])];
    p[i] = { ...p[i], [field]: value };
    setEditing({ ...editing, positions: p });
  };

  return (
    <div>
      <div className="sp-page-header">
        <div>
          <h1 className="sp-page-title">Angebote</h1>
          <p className="sp-page-subtitle">{offers.length} Angebote insgesamt</p>
        </div>
        <button onClick={() => setEditing({ number: '', projectId: '', positions: [emptyPos()] })} className="sp-btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Neues Angebot
        </button>
      </div>

      {editing && (
        <div className="sp-form-card">
          <h3 className="sp-form-title">{editing.id ? 'Angebot bearbeiten' : 'Neues Angebot'}</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="sp-label">Angebotsnr. *</label>
              <input value={editing.number || ''} onChange={(e) => setEditing({ ...editing, number: e.target.value })}
                className="sp-input" placeholder="AG-2026-001" autoFocus />
            </div>
            <div>
              <label className="sp-label">Projekt *</label>
              <select value={editing.projectId || ''} onChange={(e) => setEditing({ ...editing, projectId: e.target.value })}
                className="sp-input">
                <option value="">Projekt wählen...</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
            <div>
              <label className="sp-label">MwSt. %</label>
              <input type="number" value={editing.taxRate ?? 19} onChange={(e) => setEditing({ ...editing, taxRate: +e.target.value })}
                className="sp-input" />
            </div>
          </div>

          {/* Positions */}
          <div className="mt-6 pt-5 border-t border-surface-100">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-[13px] font-semibold text-slate-700">Positionen</h4>
              <button onClick={() => setEditing({ ...editing, positions: [...(editing.positions || []), emptyPos()] })}
                className="sp-btn-ghost text-xs">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Hinzufügen
              </button>
            </div>
            <div className="bg-surface-50 rounded-xl p-4 space-y-2">
              <div className="grid grid-cols-12 gap-2 text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-1 mb-1">
                <div className="col-span-1">Pos.</div>
                <div className="col-span-4">Beschreibung</div>
                <div className="col-span-2">Einheit</div>
                <div className="col-span-2">Menge</div>
                <div className="col-span-2">EP (EUR)</div>
                <div className="col-span-1"></div>
              </div>
              {(editing.positions || []).map((pos, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 animate-fade-in">
                  <input value={pos.posNumber} onChange={(e) => updatePos(i, 'posNumber', e.target.value)}
                    className="sp-input text-xs !py-2 col-span-1" placeholder="1" />
                  <input value={pos.description} onChange={(e) => updatePos(i, 'description', e.target.value)}
                    className="sp-input text-xs !py-2 col-span-4" placeholder="Beschreibung" />
                  <input value={pos.unit} onChange={(e) => updatePos(i, 'unit', e.target.value)}
                    className="sp-input text-xs !py-2 col-span-2" />
                  <input type="number" value={pos.quantity} onChange={(e) => updatePos(i, 'quantity', +e.target.value)}
                    className="sp-input text-xs !py-2 col-span-2" />
                  <input type="number" step="0.01" value={pos.unitPrice} onChange={(e) => updatePos(i, 'unitPrice', +e.target.value)}
                    className="sp-input text-xs !py-2 col-span-2" />
                  <button onClick={() => {
                    const p = [...(editing.positions || [])]; p.splice(i, 1); setEditing({ ...editing, positions: p });
                  }} className="sp-btn-icon !w-auto hover:!text-error-500 hover:!bg-error-50 col-span-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Notizen & Gültig bis */}
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Gültig bis</label>
              <input type="date" value={(editing.validUntil || '').slice(0, 10)}
                onChange={(e) => setEditing({ ...editing, validUntil: e.target.value ? new Date(e.target.value).toISOString() : undefined })}
                className="sp-input" />
            </div>
            <div>
              <label className="sp-label">Status</label>
              <select value={editing.status || 'DRAFT'} onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                className="sp-input">
                {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="sp-label">Notizen</label>
              <textarea value={editing.notes || ''}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                className="sp-input min-h-[60px] resize-y" placeholder="Anmerkungen zum Angebot..." />
            </div>
          </div>

          <div className="sp-form-actions">
            <button onClick={() => saveMutation.mutate(editing)} disabled={saveMutation.isPending} className="sp-btn-primary">
              {saveMutation.isPending ? 'Speichern...' : 'Speichern'}
            </button>
            <button onClick={() => setEditing(null)} className="sp-btn-secondary">Abbrechen</button>
          </div>
        </div>
      )}

      <div className="sp-card overflow-hidden animate-fade-in">
        <table className="sp-table">
          <thead>
            <tr>
              <th>Nr.</th>
              <th>Projekt</th>
              <th>Status</th>
              <th className="text-right">Netto</th>
              <th className="text-right">Brutto</th>
              <th className="w-28 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6}><div className="flex items-center justify-center py-12 gap-2 text-slate-400">
                <svg className="sp-spinner" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                Laden...</div></td></tr>
            ) : offers.length === 0 ? (
              <tr><td colSpan={6}><div className="sp-empty">
                <div className="w-12 h-12 rounded-2xl bg-surface-100 flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                </div>
                <span className="text-sm font-medium">Keine Angebote vorhanden</span>
              </div></td></tr>
            ) : offers.map((o) => (
              <tr key={o.id} onClick={() => setEditing(o)} className="cursor-pointer hover:bg-brand-50/50 transition-colors">
                <td className="font-mono text-xs font-semibold text-slate-800">{o.number}</td>
                <td className="text-slate-500">{o.project?.title || '-'}</td>
                <td><span className={`sp-badge ${statusStyles[o.status] || ''}`}>{statusLabels[o.status] || o.status}</span></td>
                <td className="text-right font-mono text-xs text-slate-500">{fmt(Number(o.totalNet))}</td>
                <td className="text-right font-mono text-xs font-semibold text-slate-800">{fmt(Number(o.totalGross))} EUR</td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-0.5">
                    <a href={`/api/v1/offers/${o.id}/pdf`} target="_blank" rel="noreferrer" className="sp-btn-icon" title="PDF herunterladen">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    </a>
                    <button onClick={() => setEditing(o)} className="sp-btn-icon" title="Bearbeiten">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
